import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX } from "lucide-react";
import { ScrollReveal } from "@/components/motion/ScrollReveal";
import { KLSkyline } from "@/components/illustrations/KLSkyline";
import { RaceColorBar } from "@/components/illustrations/RaceColorBar";

/**
 * LaunchVideo — Part 2 of the home-page narrative triptych.
 *
 * Hosts the official launch video as a self-hosted, autoplaying, muted,
 * looping reel inside the cinematic frame (deep navy surface, race-coloured
 * trim, aspect-video ratio), with a "Tap for sound" button to unmute.
 *
 * Why muted autoplay + tap-to-unmute: every browser (Chrome, iOS Safari, …)
 * blocks autoplay WITH audio — sound is only permitted after a user gesture.
 * Muted is the one mode that reliably auto-starts; the unmute button is the
 * required gesture to enable audio (and doubles as a play recovery if a
 * browser blocks the muted autoplay in a normal tab).
 *
 * The video files live in `microsite/public/` and are referenced as
 * root-relative paths. A mobile-optimised cut and a web/desktop cut are
 * rendered as two `AutoplayVideo` instances toggled at the Tailwind `lg`
 * breakpoint (matching the hero poster's `lg:hidden` / `hidden lg:block`
 * convention). Sources are 720p `.mp4` (H.264), decoding across all browsers.
 *
 * The section carries the id `#launch-video` so the StoryIntro chevron
 * link can smooth-scroll to it.
 */

const VIDEO_FRAME_CLASS =
  "block aspect-video w-full overflow-hidden bg-surface-deep object-cover ring-1 ring-surface-deep-foreground/10 shadow-2xl";

type AutoplayVideoProps = {
  src: string;
  label: string;
  /** Responsive show/hide toggle for the mobile vs web cut (e.g. `lg:hidden`). */
  className?: string;
};

/**
 * A muted, looping, autoplaying `<video>` with an overlaid unmute button.
 * `muted` is set imperatively via a ref to work around a long-standing React
 * bug where the `muted` DOM property isn't reliably applied — a common cause
 * of iOS Safari refusing to autoplay.
 */
function AutoplayVideo({ src, label, className }: AutoplayVideoProps) {
  const { t } = useTranslation("home");
  const videoRef = useRef<HTMLVideoElement>(null);
  const [muted, setMuted] = useState(true);

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    // Guarantee the element is genuinely muted so browsers allow autoplay.
    el.muted = true;
    void el.play().catch(() => {
      /* Autoplay may be blocked; the unmute button recovers playback. */
    });
  }, []);

  const toggleSound = () => {
    const el = videoRef.current;
    if (!el) return;
    const next = !el.muted;
    el.muted = next;
    setMuted(next);
    if (!next) {
      // Unmuting is a user gesture, so audio (and playback) is now permitted.
      void el.play().catch(() => {});
    }
  };

  return (
    <div className={`relative mx-auto w-full max-w-5xl ${className ?? ""}`}>
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        aria-label={label}
        className={VIDEO_FRAME_CLASS}
      >
        <source src={src} type="video/mp4" />
      </video>

      <button
        type="button"
        onClick={toggleSound}
        aria-label={muted ? t("launchVideo.tapForSound") : t("launchVideo.soundOn")}
        className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 rounded-full bg-background/90 p-2 text-xs font-semibold uppercase tracking-[0.14em] text-primary shadow-lg ring-1 ring-primary/20 backdrop-blur transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary sm:bottom-4 sm:right-4 sm:px-3.5 sm:py-2"
      >
        {muted ? <VolumeX className="h-3.5 w-3.5 sm:h-4 sm:w-4" /> : <Volume2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />}
        <span className="hidden sm:inline">{muted ? t("launchVideo.tapForSound") : t("launchVideo.soundOn")}</span>
      </button>
    </div>
  );
}

export function LaunchVideo() {
  const { t } = useTranslation("home");
  return (
    <section
      id="launch-video"
      aria-label={t("launchVideo.sectionLabel")}
      className="relative bg-surface-deep text-surface-deep-foreground overflow-hidden"
    >
      {/* Soft KL skyline silhouette anchors the section in the city. */}
      <KLSkyline
        aria-hidden
        className="absolute inset-x-0 bottom-0 h-40 md:h-56 opacity-50"
        toneClassName="text-surface-deep-foreground/10"
      />

      <div className="container relative py-24 md:py-32">
        <ScrollReveal delay={0.12}>
          <div>
            {/* Cinematic frame: race-coloured trim sandwiches the video. */}
            <RaceColorBar height={4} className="rounded-t-2xl max-w-5xl mx-auto" />

            {/* Mobile cut — hidden at lg and up. */}
            <AutoplayVideo
              src="/launch-video-mobile-720.mp4"
              label={t("launchVideo.title")}
              className="lg:hidden"
            />

            {/* Web/desktop cut — hidden below lg, shown at lg and up. */}
            <AutoplayVideo
              src="/launch-video-web-720.mp4"
              label={t("launchVideo.title")}
              className="hidden lg:block"
            />

            <RaceColorBar height={4} className="rounded-b-2xl max-w-5xl mx-auto" />
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
